import { useEffect, useState } from "react";

import BoxContainer from "@hl/base-components/lib/BoxContainer";
import { LoadingPage } from "@hl/base-components/lib/LoadingPage";
import CustomPaginationWrapper from "@hl/base-components/lib/table/CustomPaginationWrapper";
import { TEXT_COLOR } from "@hl/base-components/lib/theme/colors";
import {
  Box,
  Group,
  Text,
  Modal,
  Divider,
  Button,
  Stack,
  Space,
} from "@mantine/core";
import { isEmpty } from "lodash";

import { TokenMetadataAttribute } from "apollo/graphql.generated";
import { fileTypeFromExtension, typeFromMime } from "utils/media/file";
import { CollectionAttributeFormValues } from "~/features/collections/forms/CreateCollectionMultistepForm/AddMetadataTraitsModal";
import { CreationPreviewImage } from "~/features/collections/forms/CreateCollectionMultistepForm/CollectionMediaUploader";

export const ViewEditionMetadataModal = ({
  name,
  description,
  onClose,
  mediaUrl,
  coverImageUrl = "",
  attributes,
}: ViewEditionMetadataModalProps) => {
  const [activePage, setPage] = useState(1);
  const [lowerBound, setLowerBound] = useState(1);
  const [upperBound, setUpperBound] = useState(1);
  const [loading, setLoading] = useState<boolean>(false);
  const [traits, setTraits] = useState<CollectionAttributeFormValues[]>([]);

  useEffect(() => {
    setLoading(true);

    const metadataTraits: CollectionAttributeFormValues[] = [];
    attributes.forEach((trait: { trait_type?: string; value?: string }) => {
      if (trait.trait_type && trait.value) {
        metadataTraits.push({ name: trait.trait_type, value: trait.value });
      }
    });
    setTraits(metadataTraits);
    setLoading(false);
  }, []);
  return (
    <Modal
      opened
      title={
        <Text weight={500} size="lg">
          Update metadata
        </Text>
      }
      onClose={onClose}
      size="xl"
    >
      {loading ? (
        <Box mx="auto">
          <LoadingPage marginTop={30} marginBottom={40} />
        </Box>
      ) : (
        <Stack>
          <Stack spacing={4}>
            <Text weight={500} size="sm">
              Collection name
            </Text>
            <BoxContainer>
              <Text>{name}</Text>
            </BoxContainer>
          </Stack>
          <Box>
            <Stack spacing={4}>
              <Text weight={500} size="sm">
                Collection description
              </Text>
              <BoxContainer>
                <Text>{description}</Text>
              </BoxContainer>
            </Stack>
          </Box>

          <Stack spacing="xs">
            <Text weight={500} size="sm">
              Artwork
            </Text>

            <BoxContainer>
              <Group position="left" spacing={4}>
                <Box>
                  <CreationPreviewImage
                    url={mediaUrl}
                    mediaType={typeFromMime[fileTypeFromExtension(mediaUrl)]}
                    tiny
                  />
                </Box>
              </Group>
              {!isEmpty(coverImageUrl) && (
                <>
                  <Divider my="sm" />
                  <Group position="left" spacing={4}>
                    <CreationPreviewImage
                      url={coverImageUrl!}
                      mediaType={
                        typeFromMime[fileTypeFromExtension(coverImageUrl!)]
                      }
                      tiny
                    />
                  </Group>
                </>
              )}
            </BoxContainer>
          </Stack>
          <Stack spacing={8}>
            <Text weight={500} size="sm">
              Traits
            </Text>
            <Stack spacing={0}>
              {!isEmpty(traits) && (
                <CustomPaginationWrapper
                  activePage={activePage}
                  setPage={setPage}
                  setLowerBound={setLowerBound}
                  setUpperBound={setUpperBound}
                  maxLength={Object.keys(traits).length}
                >
                  {(traits ?? [])
                    .slice(lowerBound - 1, upperBound)
                    .map(({ name, value }, key) => {
                      return (
                        <div key={key}>
                          <Space h="sm" />
                          <Group position="apart">
                            <Text>{name}</Text>
                            <Group>
                              <Text color={TEXT_COLOR.SECONDARY}>{value}</Text>
                            </Group>
                          </Group>
                          <Space h="sm" />
                        </div>
                      );
                    })}
                </CustomPaginationWrapper>
              )}
            </Stack>
          </Stack>
          <Space h={4} />
          <Divider mx={-16} />
          <Button mt="md" fullWidth size="xl" onClick={onClose}>
            Done
          </Button>
        </Stack>
      )}
    </Modal>
  );
};

export interface ViewEditionMetadataModalProps {
  name: string;
  description: string;
  mediaUrl: string;
  coverImageUrl: string | null;
  onClose: () => void;
  attributes: TokenMetadataAttribute[];
}
